import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Settings, Zap } from "lucide-react";
import { toast } from "sonner";

import CarregandoRelatorio from "../relatorios-ofertas/comum/Carregando";

import { Button } from "../../components/ui/button";

import BarrasPorDia from "../ofertas-do-dia/admin/BarrasPorDia";
import { reais } from "../ofertas-do-dia/admin/formato";
import { ROTA_HUB_RELATORIOS } from "../ofertas-do-dia/admin/rotas";
import { carregarRelatorio } from "./api";
import Funil from "./relatorio/Funil";
import MotivosNaoEnvio from "./relatorio/MotivosNaoEnvio";
import Numero from "./relatorio/Numero";
import { ROTA_CONFIG_RECUPERADOR } from "./rotas";
import { CarregandoBloco, CarregandoCards } from "../../components/carregando";
import { Skeleton } from "../../components/ui/skeleton";

const PERIODOS = [7, 30, 90];
const hojeMenos = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return d.toLocaleDateString("sv-SE"); };

// Relatórios → Relatório de Ofertas → Campanhas → Recuperador de Vendas.
export default function RelatorioRecuperadorPage() {
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [rel, setRel] = useState(null);
  // Erro na 1ª leitura: aviso com "Tentar de novo" (antes piscava para sempre);
  // trocar o período mantém os dados na tela até os novos chegarem (25/10/2026).
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    carregarRelatorio({ inicio: hojeMenos(dias - 1), fim: hojeMenos(0) })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório"); } });
    return () => { vivo = false; };
  }, [dias, tentativa]);

  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6" data-e2e="relatorio-recuperador">
        <div>
          <button type="button" onClick={() => navigate(ROTA_HUB_RELATORIOS)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Relatório de Campanhas
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Relatório do Recuperador de Vendas</h1>
                {rel ? (
                  <p className="text-sm text-muted-foreground">{(rel.is_active ? "Recuperador ligado" : "Recuperador desligado — histórico abaixo")}</p>
                ) : <Skeleton className="h-4 w-48 mt-1" />}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border p-0.5 bg-card">
                {PERIODOS.map((p) => (
                  <button key={p} type="button" onClick={() => setDias(p)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md ${dias === p ? "bg-amber-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                    {p} dias
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(ROTA_CONFIG_RECUPERADOR)}>
                <Settings className="w-4 h-4 mr-1.5" /> Configurar
              </Button>
            </div>
          </div>
        </div>

        {!rel && erro ? <CarregandoRelatorio erro onTentar={() => setTentativa((t) => t + 1)} /> : !rel ? (
          // Carregamento com cards (25/10/2026): os 5 números, o funil e os gráficos piscando.
          <div className="space-y-6" aria-busy="true" aria-label="Carregando">
            <CarregandoCards quantos={5} className="grid grid-cols-2 lg:grid-cols-5 gap-3" />
            <div className="grid lg:grid-cols-2 gap-4"><CarregandoBloco altura="h-72" /><CarregandoBloco altura="h-72" /></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Numero rotulo="Mensagens enviadas" valor={r.enviadas} atual={r.enviadas} anterior={a.enviadas} e2e="num-enviadas" />
              <Numero rotulo="Pedidos recuperados" valor={r.pedidos} atual={r.pedidos} anterior={a.pedidos} e2e="num-pedidos" />
              <Numero rotulo="Receita recuperada" valor={reais(r.receita)} atual={r.receita} anterior={a.receita} destaque e2e="num-receita" />
              <Numero rotulo="Conversão" valor={`${String(r.conversao_pct).replace(".", ",")}%`} atual={r.conversao_pct} anterior={a.conversao_pct} e2e="num-conversao" />
              <Numero rotulo="Ticket médio" valor={reais(r.ticket_medio)} atual={r.ticket_medio} anterior={a.ticket_medio} e2e="num-ticket" />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-3">
              Setas: comparação com os {dias} dias anteriores. Venda recuperada = pedido do mesmo cliente em até 2h depois da mensagem da CLEO. Pedido cancelado não conta.
            </p>
            <div className="grid lg:grid-cols-2 gap-4">
              <Funil funil={rel.funil} medidoDesde={rel.funil_medido_desde} inicioPeriodo={rel.periodo.inicio} />
              <section className="rounded-xl border bg-card p-4 sm:p-5 space-y-5">
                <div>
                  <h3 className="font-semibold">Receita recuperada por dia</h3>
                  <BarrasPorDia serie={rel.por_dia} valor={(d) => d.receita} rotulo="Receita recuperada por dia" />
                </div>
                <div>
                  <h3 className="font-semibold">Mensagens por dia</h3>
                  <BarrasPorDia serie={rel.por_dia} valor={(d) => d.enviadas} rotulo="Mensagens enviadas por dia" />
                </div>
              </section>
            </div>
            <MotivosNaoEnvio motivos={rel.nao_enviadas} />
          </>
        )}
      </div>
    </div>
  );
}
